/**
 * Page publique de paiement parent.
 *
 * Accessible sans compte — le parent y arrive via un lien partagé par l'élève.
 * URL : /parent-pay?token=<uuid>[&paid=1]
 *
 * Flow :
 *   1. Charge les infos du lien via Edge Function get-parent-link (GET)
 *   2. Affiche l'offre + prix
 *   3. Bouton "Payer" → Edge Function create-parent-checkout → redirect Stripe
 *   4. Retour avec ?paid=1 → affiche confirmation
 */

import { useState, useEffect } from 'react';
import { supabase, supabaseUrl, supabaseAnonKey } from '@/lib/supabase';
import { isNativeApp } from '@/lib/platform';
import { markCheckoutStarted, passCheckoutMark } from '@/lib/analytics/track';
import { useOfferSettings } from '@/lib/offers/offer-settings';
import { OtpAuthForm } from '@/components/otp-auth-form';

type LinkInfo = {
  offer_type: 'normal' | 'epreuves';
  duration: '24h' | '7d' | '30d';
  price_cents: number;
  student_name: string | null;
};

type PageState =
  | { status: 'loading' }
  | { status: 'error'; message: string }
  | { status: 'ready'; link: LinkInfo }
  | { status: 'paid' };

const OFFER_LABELS: Record<'normal' | 'epreuves', string> = {
  normal: 'Accès exercices',
  epreuves: 'Accès épreuves officielles',
};

const DURATION_LABELS: Record<'24h' | '7d' | '30d', { title: string; sub: string }> = {
  '24h': { title: '24 heures', sub: 'Accès unique' },
  '7d': { title: '7 jours', sub: 'Abonnement · résiliable' },
  '30d': { title: '30 jours', sub: 'Abonnement · résiliable' },
};

function formatPrice(cents: number) {
  return (cents / 100).toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });
}

async function fetchLinkInfo(token: string): Promise<LinkInfo> {
  const res = await fetch(
    `${supabaseUrl}/functions/v1/get-parent-link?token=${encodeURIComponent(token)}`,
    { headers: { apikey: supabaseAnonKey } },
  );
  const data = await res.json();
  if (!res.ok) throw new Error(data.error ?? 'Lien introuvable');
  return data as LinkInfo;
}

export function ParentPayPage() {
  const params = new URLSearchParams(window.location.search);
  const token = params.get('token') ?? '';
  const paid = params.get('paid') === '1';
  const sessionId = params.get('session_id');

  const [state, setState] = useState<PageState>(paid ? { status: 'paid' } : { status: 'loading' });
  const offerSettings = useOfferSettings();
  const subscriptionNote = offerSettings.trial.enabled
    ? `${offerSettings.trial.days} jours gratuits, puis résiliable à tout moment`
    : 'Résiliable à tout moment';
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  // Email collecté par Stripe (lu via `get-checkout-summary`) — préremplit
  // l'OTP quand le parent choisit « Me connecter à mon espace ». Le compte a
  // déjà été créé par `stripe-webhook → ensureParentAccountAndLink`.
  const [parentEmail, setParentEmail] = useState<string | null>(null);
  const [showLogin, setShowLogin] = useState(false);

  // ── Gate natif (App Store 3.1.1 + Google Play Reader Apps policy) ─────────
  // Cette page contient un CTA Stripe direct (`create-parent-checkout`). Elle
  // est normalement ouverte via un lien externe (SMS/mail), donc en pratique
  // dans un navigateur — pas dans l'app. Mais un utilisateur pourrait taper
  // l'URL dans le webview de l'app : on bloque proactivement.
  const nativeBlocked = isNativeApp() && !paid;

  useEffect(() => {
    if (paid || nativeBlocked) return;
    if (!token) {
      setState({ status: 'error', message: 'Lien invalide.' });
      return;
    }

    fetchLinkInfo(token)
      .then((link) => setState({ status: 'ready', link }))
      .catch((err: Error) => {
        const msg =
          err.message === 'Lien déjà utilisé'
            ? "Ce lien a déjà été utilisé — l'accès est déjà activé."
            : err.message === 'Lien expiré'
              ? "Ce lien a expiré. Demande à ton enfant d'en générer un nouveau."
              : err.message;
        setState({ status: 'error', message: msg });
      });
  }, [token, paid, nativeBlocked]);

  // Post-paiement : récupère l'email collecté par Stripe via get-checkout-summary
  // (endpoint public, pas d'auth). Utilisé pour préremplir + verrouiller l'OTP
  // de connexion à l'espace parent (le compte a été créé par le webhook).
  useEffect(() => {
    if (!paid || !sessionId) return;
    fetch(
      `${supabaseUrl}/functions/v1/get-checkout-summary?session_id=${encodeURIComponent(sessionId)}`,
      { headers: { apikey: supabaseAnonKey } },
    )
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data?.email) setParentEmail(data.email as string);
      })
      .catch(() => {
        /* silencieux — le CTA reste sans email prérempli */
      });
  }, [paid, sessionId]);

  // Rendu conditionnel APRÈS tous les hooks (rules-of-hooks).
  if (nativeBlocked) {
    return (
      <div className="bg-ink-50 flex min-h-dvh items-center justify-center px-4 py-12">
        <div className="w-full max-w-sm space-y-5 text-center">
          <p className="font-display text-primary-600 text-2xl font-extrabold tracking-tight">
            Jeraisonne
          </p>
          <div className="rounded-2xl bg-white p-6 shadow-sm ring-1 ring-black/5">
            <p className="text-3xl">🌐</p>
            <h2 className="font-display text-ink-900 mt-3 text-lg font-extrabold">
              Ouvre ce lien dans ton navigateur
            </h2>
            <p className="text-ink-500 mt-2 font-sans text-sm leading-relaxed">
              Le paiement pour Jeraisonne se fait depuis un navigateur web. Copie le lien reçu et
              ouvre-le dans Safari (ou tout autre navigateur) sur cet appareil ou un autre.
            </p>
          </div>
        </div>
      </div>
    );
  }

  async function handlePay() {
    setCheckoutLoading(true);
    // Pass ponctuel — l'enfant a demandé cette formule dans son écran d'accès.
    if (state.status === 'ready') {
      markCheckoutStarted(passCheckoutMark(state.link.offer_type, state.link.duration));
    }
    const { data, error } = await supabase.functions.invoke('create-parent-checkout', {
      body: { token, kind: 'pass' },
    });
    if (error || !data?.url) {
      setCheckoutLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  async function handleSubscribe(plan: 'student_monthly' | 'family_monthly') {
    setCheckoutLoading(true);
    // Abonnement mensuel Élève/Famille — au NOM DU PARENT, l'enfant reçoit un
    // siège (target_child_id géré côté webhook via le token). Alternative long
    // terme aux passes ponctuels.
    markCheckoutStarted(plan === 'family_monthly' ? 'abonnement-famille' : 'abonnement-eleve');
    const { data, error } = await supabase.functions.invoke('create-parent-checkout', {
      body: { token, kind: 'subscription', plan },
    });
    if (error || !data?.url) {
      setCheckoutLoading(false);
      return;
    }
    window.location.href = data.url as string;
  }

  return (
    <div className="bg-ink-50 flex min-h-dvh items-center justify-center px-4 py-12">
      <div className="w-full max-w-sm space-y-6">
        {/* Logo */}
        <div className="text-center">
          <p className="font-display text-primary-600 text-2xl font-extrabold tracking-tight">
            Jeraisonne
          </p>
          <p className="text-ink-500 font-sans text-sm">Plateforme de révision scolaire</p>
        </div>

        {/* Card principale */}
        <div className="rounded-2xl bg-white shadow-sm ring-1 ring-black/5">
          {state.status === 'loading' && (
            <div className="flex h-48 items-center justify-center">
              <div className="border-primary-400 h-8 w-8 animate-spin rounded-full border-2 border-t-transparent" />
            </div>
          )}

          {state.status === 'error' && (
            <div className="space-y-3 p-6 text-center">
              <p className="text-2xl">⚠️</p>
              <p className="text-ink-700 font-sans font-semibold">{state.message}</p>
            </div>
          )}

          {state.status === 'paid' && !showLogin && (
            <div className="space-y-4 p-6">
              <div className="space-y-3 text-center">
                <p className="text-4xl">✅</p>
                <h2 className="font-display text-ink-900 text-xl font-extrabold">Accès activé !</h2>
                <p className="text-ink-500 font-sans text-sm leading-relaxed">
                  Ton enfant peut maintenant continuer ses révisions sur Jeraisonne.
                </p>
              </div>
              <div className="border-primary-100 bg-primary-50/40 space-y-3 rounded-xl border p-4">
                <p className="font-display text-ink-900 text-sm font-bold">
                  Un espace parent a été créé pour vous
                </p>
                <p className="text-ink-500 font-sans text-xs leading-relaxed">
                  Connectez-vous
                  {parentEmail ? (
                    <>
                      {' avec '}
                      <span className="text-ink-700 break-all font-semibold">{parentEmail}</span>
                    </>
                  ) : null}{' '}
                  pour gérer cet achat, prolonger l'accès de votre enfant, ou souscrire un
                  abonnement famille.
                </p>
                <button
                  onClick={() => setShowLogin(true)}
                  className="bg-primary-400 hover:bg-primary-500 font-display min-h-[2.75rem] w-full rounded-xl py-3 text-sm font-bold text-white shadow transition-all active:scale-[.98]"
                >
                  Me connecter à mon espace parent
                </button>
              </div>
            </div>
          )}

          {state.status === 'paid' && showLogin && (
            <div className="space-y-4 p-6">
              <OtpAuthForm
                initialMode="login"
                lockedEmail={parentEmail ?? undefined}
                hideOAuth
                hideModeToggle
              />
              <button
                onClick={() => setShowLogin(false)}
                className="text-ink-500 hover:text-ink-700 w-full py-2 font-sans text-xs font-semibold"
              >
                ← Retour
              </button>
            </div>
          )}

          {state.status === 'ready' && (
            <div className="space-y-5 p-6">
              <div className="space-y-1">
                <p className="text-primary-500 font-sans text-[11px] font-semibold uppercase tracking-wider">
                  Paiement pour
                </p>
                <h2 className="font-display text-ink-900 text-xl font-extrabold">
                  {state.link.student_name ?? 'Ton enfant'}
                </h2>
              </div>

              {offerSettings.passes[state.link.duration] ? (
                <>
                  {/* Récapitulatif offre */}
                  <div className="border-ink-100 space-y-3 rounded-xl border p-4">
                    <div className="flex items-center justify-between">
                      <span className="text-ink-600 font-sans text-sm">Offre</span>
                      <span className="text-ink-900 font-sans text-sm font-semibold">
                        {OFFER_LABELS[state.link.offer_type]}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-ink-600 font-sans text-sm">Durée</span>
                      <div className="text-right">
                        <p className="text-ink-900 font-sans text-sm font-semibold">
                          {DURATION_LABELS[state.link.duration].title}
                        </p>
                        <p className="text-ink-400 font-sans text-[11px]">
                          {DURATION_LABELS[state.link.duration].sub}
                        </p>
                      </div>
                    </div>
                    <div className="border-ink-100 flex items-center justify-between border-t pt-3">
                      <span className="text-ink-600 font-sans text-sm font-semibold">Total</span>
                      <span className="font-display text-ink-900 text-xl font-extrabold">
                        {formatPrice(state.link.price_cents)}
                      </span>
                    </div>
                  </div>

                  <button
                    onClick={handlePay}
                    disabled={checkoutLoading}
                    className="bg-primary-400 hover:bg-primary-500 font-display min-h-[2.75rem] w-full rounded-xl py-3.5 text-sm font-bold text-white shadow transition-all active:scale-[.98] disabled:opacity-60"
                  >
                    {checkoutLoading
                      ? 'Redirection…'
                      : `Payer ${formatPrice(state.link.price_cents)}`}
                  </button>

                  <p className="text-ink-400 text-center font-sans text-xs leading-relaxed">
                    Paiement sécurisé via Stripe.
                    {state.link.duration !== '24h' &&
                      ' Résiliable à tout moment depuis votre espace Stripe.'}
                  </p>
                </>
              ) : (
                <p className="text-ink-600 bg-ink-50 rounded-xl p-3 font-sans text-sm">
                  Cette formule n’est plus proposée. Choisissez un abonnement ci-dessous.
                </p>
              )}

              {/* ── Alternative long terme : abonnement Élève/Famille ─────── */}
              {/* Le parent souscrit à son NOM (avec un compte créé au passage),
                  l'enfant reçoit un siège. Idéal si le parent veut couvrir 1..3
                  enfants durablement plutôt qu'acheter des passes ponctuels. */}
              <div className="flex items-center gap-2 pt-2">
                <div className="border-ink-200 flex-1 border-t" />
                <span className="text-ink-400 font-sans text-xs">ou</span>
                <div className="border-ink-200 flex-1 border-t" />
              </div>

              <div className="space-y-2">
                <p className="text-ink-700 font-sans text-xs font-semibold">
                  Abonnement mensuel (au nom du parent)
                </p>
                <button
                  onClick={() => handleSubscribe('student_monthly')}
                  disabled={checkoutLoading}
                  className="border-primary-200 hover:border-primary-300 flex min-h-[2.75rem] w-full items-center justify-between rounded-xl border-2 bg-white px-4 py-3 text-left transition-all active:scale-[.98] disabled:opacity-60"
                >
                  <div>
                    <p className="font-display text-ink-900 text-sm font-bold">Élève · 1 enfant</p>
                    <p className="text-ink-500 font-sans text-xs">{subscriptionNote}</p>
                  </div>
                  <p className="font-display text-ink-900 text-sm font-extrabold">9,99 €/mois</p>
                </button>
                <button
                  onClick={() => handleSubscribe('family_monthly')}
                  disabled={checkoutLoading}
                  className="border-primary-200 hover:border-primary-300 flex min-h-[2.75rem] w-full items-center justify-between rounded-xl border-2 bg-white px-4 py-3 text-left transition-all active:scale-[.98] disabled:opacity-60"
                >
                  <div>
                    <p className="font-display text-ink-900 text-sm font-bold">
                      Famille · jusqu'à 3 enfants
                    </p>
                    <p className="text-ink-500 font-sans text-xs">{subscriptionNote}</p>
                  </div>
                  <p className="font-display text-ink-900 text-sm font-extrabold">29,99 €/mois</p>
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
